import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportDownloadDetail } from '../interfaces/ReportDownloadDetail';
import useNswagClient from './../../../hooks/api/useNswagClient';
import { prepareReports } from '../helpers/ReportingHelper';
import LoadingWheel from './../../../components/ui/LoadingWheel';
import { Box, Button, Chip, SvgIcon, Typography, useTheme } from '@mui/material';
import { BookClosed, DownloadCloud01 } from './../../../assets';
import CustomPagination from './../../../components/forms/Pagination';
import { ConfigurationName } from './../../../enums/ConfigurationName';
import { useConfigurations } from './../../../components/shared/useConfigurations';
import { Pill } from './../../../components/shared/Pill';
import { ReportStatus, ReportStatusTranslationMap, getReportStatusPalette } from './../../../enums/ReportStatus';
import { IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import { RefreshCw05, Trash01 } from './../../../assets';
import { CloseDialogAction } from './../../../components/shared/dialogs/CloseDialogAction';
import useLogError from './../../../hooks/useLogError';
import { TableColumn, TableSection } from './../../../components/shared/tables/TableSection';

export interface ReportHistoryTabHandle {
  refresh: () => void;
}

interface ReportHistoryTabProps {
  onLoadingChange?: (isLoading: boolean) => void;
  // Opt-in: show a delete action per report (Reports V2). Old /reports page leaves this off.
  canDelete?: boolean;
  // Opt-in: render the V2 table layout. Legacy /reports keeps the original card list.
  variant?: 'cards' | 'table';
  // Opt-in: render a refresh button at the far right of the filter row (Reports V2 table).
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const ReportHistoryTab = forwardRef<ReportHistoryTabHandle, ReportHistoryTabProps>(({ onLoadingChange, canDelete = false, variant = 'cards', onRefresh, isRefreshing = false }, ref) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { isConfigurationEnabled } = useConfigurations();
  const [reports, setReports] = useState<ReportDownloadDetail[]>([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [downloadReportId, setDownloadReportId] = useState(0);
  const showReportStatus = isConfigurationEnabled(ConfigurationName.ReportProgressStatusEnabled);
  const { getReportsAvailableForDownload, getReport, deleteReport } = useNswagClient();
  const { logError } = useLogError();
  const [pendingDelete, setPendingDelete] = useState<ReportDownloadDetail | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // null = all statuses; otherwise filter to a single ReportStatus.
  const [statusFilter, setStatusFilter] = useState<ReportStatus | null>(null);

  useImperativeHandle(ref, () => ({
    refresh: getReports,
  }));

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]);

  useEffect(() => {
    getReports();
  }, []);

  const getReports = () => {
    setIsLoading(true);
    getReportsAvailableForDownload().then((response) => {
      if (response?.data?.reports) {
        const reportDetails = prepareReports(response.data.reports);

        setReports(reportDetails);
      }
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  // Always enforced, independent of showReportStatus (which only gates the status pill display).
  const isDownloadable = (item: ReportDownloadDetail) => {
    return item.status === undefined || item.status === ReportStatus.Completed;
  };

  // Without the status pill there's no way to show why a report can't be downloaded yet, so hide it instead.
  const displayedReports = () => {
    const base = showReportStatus ? reports : reports.filter(isDownloadable);
    return statusFilter === null ? base : base.filter((r) => r.status === statusFilter);
  };

  const handleStatusFilter = (status: ReportStatus | null) => {
    setStatusFilter(status);
    setPage(0);
  };

  const maxPages = () => {
    return Math.ceil(displayedReports().length / rowsPerPage);
  };

  const visibleRows = () => {
    return displayedReports().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const downloadReport = (name: string, id: number) => {
    setDownloadReportId(id);
    getReport(id).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));

      const fileName = `${name}.xlsx`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    }).catch((error) => {
      console.log(error);
    }).finally(() => {
      setDownloadReportId(0);
    });
  };

  const confirmDelete = () => {
    if (!pendingDelete?.id) {
      return;
    }
    setIsDeleting(true);
    deleteReport(pendingDelete.id)
      .then(() => {
        setPendingDelete(null);
        getReports();
      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const tableColumns: TableColumn<ReportDownloadDetail>[] = [
    {
      id: 'report',
      label: t('reportingV2.table.report'),
      render: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SvgIcon sx={{ width: '20px', color: theme.palette.custom.gray[500], flexShrink: 0 }}>
            <BookClosed />
          </SvgIcon>
          {item.displayName?.includes('.') ? t(item.displayName) : (item.displayName ?? item.name ?? '')}
        </Box>
      ),
    },
    {
      id: 'period',
      label: t('reportingV2.table.period'),
      render: (item) => (item.dateFrom && item.dateTo) ? `${item.dateFrom} - ${item.dateTo}` : '—',
    },
    {
      id: 'created',
      label: t('reportingV2.table.created'),
      field: 'createdAt',
    },
    ...(showReportStatus ? [{
      id: 'status',
      label: t('reportingV2.table.status'),
      render: (item: ReportDownloadDetail) => item.status !== undefined
        ? <Pill text={t(ReportStatusTranslationMap[item.status])}
          pallet={getReportStatusPalette(item.status, theme)} />
        : null,
    }] : []),
    {
      id: 'actions',
      label: t('reportingV2.table.actions'),
      align: 'right',
      render: (item) => (
        <>
          <IconButton
            aria-label={t('reporting.download')}
            onClick={() => downloadReport(item.name ?? '', item.id ?? 0)}
            disabled={downloadReportId === item.id || !isDownloadable(item)}
            color="primary"
            size="small">
            <SvgIcon sx={{ width: '20px' }}><DownloadCloud01 /></SvgIcon>
          </IconButton>
          {canDelete && (item.status === undefined || item.status === ReportStatus.Completed || item.status === ReportStatus.Failed) && (
            <IconButton
              aria-label={t('reportingV2.delete-report')}
              onClick={() => setPendingDelete(item)}
              color="error"
              size="small"
              sx={{ ml: '4px' }}>
              <SvgIcon sx={{ width: '20px' }}><Trash01 /></SvgIcon>
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const hasDisplayedReports = displayedReports().length > 0;

  // Wait for the config flag too, otherwise rows flash in/out once showReportStatus resolves after the initial render.
  return (
    <>
      {
        variant === 'cards' && isLoading ?
          <LoadingWheel /> :
          <>
            {variant === 'cards' && visibleRows().map((item) => (
              <Box key={item.id}
                sx={{
                  display: 'flex',
                  width: '100%',
                  minHeight: '60px',
                  p: '12px',
                  alignItems: 'center',
                  justifyContent: 'left',
                  mb: '12px',
                  border: `solid 1px ${theme.palette.custom.gray[200]}`,
                  borderRadius: '4px',
                }}>
                <SvgIcon sx={{ width: '20px', mr: '24px' }}><BookClosed /></SvgIcon>
                <Typography variant="textMD"
                  sx={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'left', textAlign: 'left', overflow: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <span>
                      {(item.displayName?.includes('.') ? t(item.displayName) : (item.displayName ?? item.name ?? ''))} {t('reporting.created-at')} {item.createdAt}
                      {(item.dateFrom && item.dateTo) && ` for ${item.dateFrom} - ${item.dateTo}`}
                    </span>
                    {showReportStatus && item.status !== undefined &&
                      <Pill text={t(ReportStatusTranslationMap[item.status])}
                        pallet={getReportStatusPalette(item.status, theme)} />
                    }
                  </Box>
                  {item.storeName}
                </Typography>
                <Button size='lg'
                  onClick={() => downloadReport(item.name ?? '', item.id ?? 0)}
                  disabled={downloadReportId === item.id || !isDownloadable(item)}
                  startIcon={<SvgIcon><DownloadCloud01 /></SvgIcon>}>
                  {t('reporting.download')}
                </Button>
              </Box>
            ))}
            {variant === 'table' && (
              <TableSection<ReportDownloadDetail>
                title={t('reporting.reports')}
                columns={tableColumns}
                rows={visibleRows()}
                getRowId={(item, index) => item.id ?? index}
                recordCount={displayedReports().length}
                loading={isLoading}
                page={hasDisplayedReports ? page : undefined}
                maxPages={hasDisplayedReports ? maxPages() : undefined}
                onPageChange={hasDisplayedReports ? setPage : undefined}
                tableSx={{ minWidth: 760 }}
                actions={(
                  <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {showReportStatus && (
                      <>
                        <Chip
                          label={t('reportingV2.table.all-statuses')}
                          size="small"
                          variant={statusFilter === null ? 'filled' : 'outlined'}
                          color={statusFilter === null ? 'primary' : 'default'}
                          onClick={() => handleStatusFilter(null)}
                        />
                        {[ReportStatus.Queued, ReportStatus.Processing, ReportStatus.Completed, ReportStatus.Failed].map((status) => (
                          <Chip
                            key={status}
                            label={t(ReportStatusTranslationMap[status])}
                            size="small"
                            variant={statusFilter === status ? 'filled' : 'outlined'}
                            color={statusFilter === status ? 'primary' : 'default'}
                            onClick={() => handleStatusFilter(status)}
                          />
                        ))}
                      </>
                    )}
                    {onRefresh && (
                      <Tooltip title={t('reporting.refresh')}>
                        <span>
                          <IconButton onClick={onRefresh}
                            disabled={isRefreshing}
                            aria-label={t('reporting.refresh')}
                            size="small">
                            <SvgIcon sx={isRefreshing ? { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } } : undefined}>
                              <RefreshCw05 />
                            </SvgIcon>
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                )}
              />
            )}
            {variant === 'cards' && (
              <Box>
                {visibleRows().length > 0 ?
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    maxPages={maxPages()}
                    breakpointForChangeDisplay={120000}
                  />
                  : variant === 'cards' ? t('noData') : null}
              </Box>
            )}
            <Dialog open={pendingDelete !== null}
              onClose={() => setPendingDelete(null)}
              maxWidth='sm'
              fullWidth>
              <DialogTitle>
                {t('reportingV2.delete-report')}
                <CloseDialogAction onCancel={() => setPendingDelete(null)} />
              </DialogTitle>
              <DialogContent>
                <Typography variant='textMD'>
                  {t('reportingV2.confirm-delete')}{t(pendingDelete?.displayName ?? pendingDelete?.name ?? '')}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button variant="secondary"
                  onClick={() => setPendingDelete(null)}>
                  {t('cancel')}
                </Button>
                <Button onClick={confirmDelete}
                  variant="bad"
                  disabled={isDeleting}>
                  <Trash01 />
                  {t('confirm')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
      }
    </>
  );
});

ReportHistoryTab.displayName = 'ReportHistoryTab';

export default ReportHistoryTab;